import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import { SEARCH_SPACE_CONTEXTS, SEARCH_SPACE_CONTEXTS_COUNT } from "./gql";
import { SpaceDetailsContext } from "./types";

type UseSpaceDetailsContextsProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsContexts = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsContextsProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    SEARCH_SPACE_CONTEXTS,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    SEARCH_SPACE_CONTEXTS_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadContexts = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.searchSpaceContexts;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const contexts = useMemo<SpaceDetailsContext[]>(() => {
    const edges =
      data?.searchSpaceContexts?.edges || previousData?.searchSpaceContexts?.edges || [];

    return edges.map(({ inherited, node }) => ({
      id: node.id,
      name: node.name,
      description: node.description,
      inheritedFrom: inherited
        ? { id: node.spaceDetails.id, name: node.spaceDetails.name }
        : null,
    }));
  }, [data?.searchSpaceContexts?.edges, previousData?.searchSpaceContexts?.edges]);

  const totalCount =
    countData?.searchSpaceContextsSuggestions.filteredCount ??
    previousCountData?.searchSpaceContextsSuggestions.filteredCount ??
    0;

  return {
    loadContexts,
    error,
    contexts,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.searchSpaceContexts?.pageInfo.endCursor,
    startCursor: data?.searchSpaceContexts?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsContexts;
